import { createContext, useContext } from 'react';
import { createStore, useStore } from 'zustand';
import { devtools } from 'zustand/middleware';

const AUTO_HIDE_MS = 3000;

interface ChromeState {
  /** Record of active pin reasons (e.g. { rating: true, recall: true }). */
  pins: Record<string, true>;
  /** Whether the chrome is currently hidden (DOM-driven, not React state). */
  hidden: boolean;
}

interface ChromeActions {
  /** Bind the container element (called once via ref callback). */
  setContainer: (el: HTMLElement | null) => void;
  /** Add a pin reason — chrome stays visible while any pin exists. */
  pin: (reason: string) => void;
  /** Remove a pin reason — restarts auto-hide if no pins remain. */
  unpin: (reason: string) => void;
  /** Toggle chrome visibility (user tap). */
  toggle: () => void;
  /** Show chrome and restart the auto-hide countdown. */
  reveal: () => void;
  /** Clear the auto-hide timer (call on unmount). */
  dispose: () => void;
}

type ChromeStore = ChromeState & ChromeActions;

function applyHidden(container: HTMLElement | null, hidden: boolean) {
  if (!container) return;
  if (hidden) {
    container.setAttribute('data-chrome-hidden', '');
  } else {
    container.removeAttribute('data-chrome-hidden');
  }
}

export function createChromeStore() {
  // Closure variables — never stored in Zustand state, never trigger subscriber notifications.
  let container: HTMLElement | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let initialized = false;

  return createStore<ChromeStore>()(
    devtools(
      (set, get) => {
        function isPinned() {
          return Object.keys(get().pins).length > 0;
        }

        function scheduleHide() {
          clearTimeout(timer);
          timer = setTimeout(() => {
            if (isPinned()) return;
            set({ hidden: true }, false, 'auto-hide');
            applyHidden(container, true);
          }, AUTO_HIDE_MS);
        }

        function show() {
          set({ hidden: false }, false, 'show');
          applyHidden(container, false);
        }

        return {
          pins: {},
          hidden: false,

          setContainer(el) {
            container = el;
            if (el) {
              // First attach: show + start auto-hide timer.
              // Subsequent attaches (page navigation): preserve current state.
              if (!initialized) {
                initialized = true;
                show();
                scheduleHide();
              } else {
                applyHidden(el, get().hidden);
              }
            }
          },

          pin(reason) {
            set(
              { pins: { ...get().pins, [reason]: true } },
              false,
              `pin/${reason}`,
            );
            clearTimeout(timer);
            show();
          },

          unpin(reason) {
            if (!(reason in get().pins)) return;
            const { [reason]: _, ...rest } = get().pins;
            set({ pins: rest }, false, `unpin/${reason}`);
            if (Object.keys(rest).length === 0) {
              show();
              scheduleHide();
            }
          },

          toggle() {
            if (isPinned()) return;
            if (get().hidden) {
              show();
              scheduleHide();
            } else {
              clearTimeout(timer);
              set({ hidden: true }, false, 'toggle-hide');
              applyHidden(container, true);
            }
          },

          reveal() {
            show();
            if (!isPinned()) scheduleHide();
          },

          dispose() {
            clearTimeout(timer);
          },
        };
      },
      { name: 'chrome-visibility', enabled: import.meta.env.DEV },
    ),
  );
}

/** One store per PageReaderLayout instance (created in the component). */
export type ChromeStoreApi = ReturnType<typeof createChromeStore>;

export const useChromeStore = <T>(
  store: ChromeStoreApi,
  selector: (s: ChromeStore) => T,
) => useStore(store, selector);

// --- Chrome store context (one per PageReaderLayout) ---
export const ChromeStoreContext = createContext<ChromeStoreApi | null>(null);

export function useChromeStoreContext() {
  const store = useContext(ChromeStoreContext);
  if (!store)
    throw new Error(
      'useChromeStoreContext must be used within PageReaderLayout',
    );
  return store;
}
